import { geolocateWithRetry } from './geolocation';
// Map utility functions extracted from App.tsx

export interface Viewport {
  latitude: number;
  longitude: number;
  zoom: number;
}

export interface Supercharger {
  latitude: number;
  longitude: number;
  [key: string]: any;
}

export interface Restaurant {
  latitude: number;
  longitude: number;
  [key: string]: any;
}

// Function to calculate optimal zoom level based on restaurant positions
export const calculateOptimalZoom = (supercharger: Supercharger, restaurants: Restaurant[]): number => {
  if (restaurants.length === 0) {
    return 12; // Default zoom if no restaurants
  }
  
  // Calculate the bounding box of all restaurants + supercharger
  let minLat = supercharger.latitude;
  let maxLat = supercharger.latitude;
  let minLng = supercharger.longitude;
  let maxLng = supercharger.longitude;
  
  restaurants.forEach(restaurant => {
    minLat = Math.min(minLat, restaurant.latitude);
    maxLat = Math.max(maxLat, restaurant.latitude);
    minLng = Math.min(minLng, restaurant.longitude);
    maxLng = Math.max(maxLng, restaurant.longitude);
  });
  
  // Calculate the span of the area
  const latSpan = maxLat - minLat;
  const lngSpan = maxLng - minLng;
  const maxSpan = Math.max(latSpan, lngSpan);
  
  // Add padding to ensure all markers fit comfortably
  const paddedSpan = maxSpan * 1.2; // 20% padding
  
  // Calculate zoom level that fits the padded area
  // Formula: zoom = log2(360 / span) - 1 (with some adjustment)
  const zoom = Math.log2(360 / paddedSpan) - 1;
  
  // Clamp to reasonable zoom range (9-15)
  const clampedZoom = Math.max(9, Math.min(15, Math.round(zoom)));
  
  console.log('🗺️ Zoom calculation:', {
    maxSpan,
    paddedSpan,
    calculatedZoom: zoom,
    finalZoom: clampedZoom,
    restaurantCount: restaurants.length
  });
  
  return clampedZoom;
};

// Function to restore the previous map viewport
export const restorePreviousViewport = (
  mapRef: React.RefObject<any>,
  previousViewport: Viewport | null,
  setPreviousViewport: (viewport: Viewport | null) => void
): void => {
  if (mapRef.current && previousViewport) {
    console.log('🗺️ Restoring previous viewport:', previousViewport);
    mapRef.current.flyTo({
      center: [previousViewport.longitude, previousViewport.latitude],
      zoom: previousViewport.zoom,
      duration: 800,
      essential: true
    });
    setPreviousViewport(null); // Clear the stored viewport
  } else {
    // If no previous viewport, fall back to user's location or default
    console.log('🗺️ No previous viewport, falling back to user location');
    if (navigator.geolocation) {
      geolocateWithRetry(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('🗺️ Using current geolocation:', { latitude, longitude });
          mapRef.current?.flyTo({
            center: [longitude, latitude],
            zoom: 10,
            duration: 800,
            essential: true
          });
        },
        (error) => {
          console.log('🗺️ Geolocation failed, using default Stockholm location');
          // Fall back to Stockholm as default
          mapRef.current?.flyTo({
            center: [18.0686, 59.3293], // Stockholm coordinates
            zoom: 10,
            duration: 800,
            essential: true
          });
        }
      );
    } else {
      // Fall back to Stockholm if geolocation not supported
      console.log('🗺️ Geolocation not supported, using default Stockholm location');
      mapRef.current?.flyTo({
        center: [18.0686, 59.3293], // Stockholm coordinates
        zoom: 10,
        duration: 800,
        essential: true
      });
    }
  }
};

// Navigation utility functions
export const openTeslaNavigationDirect = (latitude: number, longitude: number, name: string): void => {
  const teslaUrl = `https://www.tesla.com/navigation?lat=${latitude}&lng=${longitude}&name=${encodeURIComponent(name)}`;
  window.open(teslaUrl, '_blank');
};

export const openGoogleMapsNavigation = (latitude: number, longitude: number, name: string): void => {
  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving&destination_place_id=${encodeURIComponent(name)}`;
  window.open(googleUrl, '_blank');
};
